import React, { useContext, useState } from 'react';
import { Box, Card, Typography, FormControl, FormLabel, Input, Button, Alert, Divider, CssVarsProvider, CssBaseline, IconButton, useColorScheme, Tabs, TabList, Tab, Select, Option } from '@mui/joy';
import { Link, useNavigate, useLocation } from 'react-router';
import { LayoutContext } from '../context';
import { theme } from '../App';

import { Moon, Sun } from "@phosphor-icons/react";
import { APIRegister } from '../api';



export default function LoginPage({ defaultTab = 0, onBack = null }) {
    const { mode, setMode } = useColorScheme();
    const [activeTab, setActiveTab] = useState(defaultTab); // 0 - вход, 1 - регистрация
    
    // Состояния для формы входа
    const [loginEmail, setLoginEmail] = useState('');
    const [loginPassword, setLoginPassword] = useState('');
    const [loginErrors, setLoginErrors] = useState({});

    // Состояния для формы регистрации
    const [registerEmail, setRegisterEmail] = useState('');
    const [registerFirstName, setRegisterFirstName] = useState('');
    const [registerLastName, setRegisterLastName] = useState('');
    const [registerTelegram, setRegisterTelegram] = useState('');
    const [registerPassword, setRegisterPassword] = useState('');
    const [registerConfirmPassword, setRegisterConfirmPassword] = useState('');
    const [registerErrors, setRegisterErrors] = useState({});
    const [referralSource, setReferralSource] = useState(localStorage.getItem('referral_source') || '');
    
    const { login, loading } = useContext(LayoutContext);
    const navigate = useNavigate();
    const location = useLocation();

    // Получаем путь для редиректа после авторизации
    const from = location.state?.from?.pathname || '/';

    // Валидация формы входа
    const validateLoginForm = () => {
        const newErrors = {};
        
        if (!loginEmail) {
            newErrors.email = 'Email обязателен';
        } else if (!/\S+@\S+\.\S+/.test(loginEmail)) {
            newErrors.email = 'Некорректный формат email';
        }
        
        if (!loginPassword) {
            newErrors.password = 'Пароль обязателен';
        }
        
        setLoginErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    // Валидация формы регистрации
    const validateRegisterForm = () => {
        const newErrors = {};

        if (!registerEmail) {
            newErrors.email = 'Email обязателен';
        } else if (!/\S+@\S+\.\S+/.test(registerEmail)) {
            newErrors.email = 'Некорректный формат email';
        }

        if (!registerTelegram) {
            newErrors.telegram = 'Telegram username обязателен';
        }

        if (!registerPassword) {
            newErrors.password = 'Пароль обязателен';
        } else if (registerPassword.length < 8) {
            newErrors.password = 'Пароль должен содержать минимум 8 символов';
        }

        if (!registerConfirmPassword) {
            newErrors.confirmPassword = 'Подтверждение пароля обязательно';
        } else if (registerPassword !== registerConfirmPassword) {
            newErrors.confirmPassword = 'Пароли не совпадают';
        }

        setRegisterErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    // Обработка входа
    const handleLoginSubmit = async (e) => {
        e.preventDefault();
        
        if (!validateLoginForm()) return;
        
        const result = await login(loginEmail, loginPassword);

        if (result.success) {
            // Если login() запустил редирект на YooKassa (pending_plan) —
            // не вызываем navigate, браузер уже уходит на внешний URL.
            if (result.redirecting) return;
            navigate(from, { replace: true });
        } else {
            setLoginErrors({ submit: result.error });
        }
    };

    // Обработка регистрации
    const handleRegisterSubmit = async (e) => {
        e.preventDefault();
        
        if (!validateRegisterForm()) return;
        
        try {
            const response = await APIRegister({
                email: registerEmail,
                telegram_username: registerTelegram.replace(/^@/, ''),
                password: registerPassword,
                password_confirm: registerConfirmPassword,
            });

            if (response.token && response.user) {
                localStorage.removeItem('referral_source');
                // Автоматически логиним пользователя после успешной регистрации
                const loginResult = await login(registerEmail, registerPassword);

                if (loginResult.success) {
                    // Если context.login триггернул редирект на оплату — не навигируем
                    if (loginResult.redirecting) return;
                    navigate(from, { replace: true });
                } else {
                    setRegisterErrors({ submit: 'Регистрация успешна, но произошла ошибка при входе' });
                }
            } else {
                setRegisterErrors({ submit: response.error || 'Ошибка при регистрации' });
            }
        } catch (error) {
            console.error('Ошибка регистрации:', error);
            const data = error.response?.data;
            const newErrors = {};

            const getErrMsg = (val) => {
                if (!val) return '';
                if (typeof val === 'string') return val;
                if (Array.isArray(val)) return typeof val[0] === 'string' ? val[0] : JSON.stringify(val[0]);
                return JSON.stringify(val);
            };

            if (data?.details) {
                if (data.details.email) newErrors.email = getErrMsg(data.details.email);
                if (data.details.first_name) newErrors.firstName = getErrMsg(data.details.first_name);
                if (data.details.last_name) newErrors.lastName = getErrMsg(data.details.last_name);
                if (data.details.telegram_username) newErrors.telegram = getErrMsg(data.details.telegram_username);
                if (data.details.password) newErrors.password = getErrMsg(data.details.password);
                if (data.details.password_confirm) newErrors.confirmPassword = getErrMsg(data.details.password_confirm);
                if (data.details.non_field_errors) newErrors.submit = getErrMsg(data.details.non_field_errors);
            }

            if (Object.keys(newErrors).length === 0) {
                newErrors.submit = typeof data?.error === 'string' ? data.error : 'Ошибка при регистрации';
            }

            setRegisterErrors(newErrors);
        }
    };
    
    // Функция для переключения темы
    const toggleTheme = () => {
        if (mode === 'light') {
        setMode('dark');
        } else {
        setMode('light');
        }
    };

    // Очистка ошибок при переключении вкладок
    const handleTabChange = (event, newValue) => {
        setActiveTab(newValue);
        setLoginErrors({});
        setRegisterErrors({});
    };

    return (
        <Box
            sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                minHeight: '100vh',
                backgroundColor: 'background.level1',
                p: 2,
            }}
        >
            <Card
                variant="outlined"
                sx={{
                width: '100%',
                maxWidth: 400,
                p: 4,
                }}
            >
                {onBack && (
                    <Button
                        variant="plain"
                        color="neutral"
                        size="sm"
                        onClick={onBack}
                        sx={{ position: 'absolute', left: 12, top: 12 }}
                    >
                        ← Назад
                    </Button>
                )}
                <Typography level="h2" component="h1" sx={{ mb: 2, textAlign: 'center' }}>
                    Вход в систему
                </Typography>

                {/* Кнопка переключения темы */}
                <IconButton
                    variant="outlined"
                    color="neutral"
                    size="sm"
                    onClick={toggleTheme}
                    sx={{
                        mr: 1,
                        position: "absolute",
                        right: 0,
                        top: 10
                    }}
                >
                    {mode === 'light' ? <Moon size={18} /> : <Sun size={18} />}
                </IconButton>
                
                <Tabs value={activeTab} onChange={handleTabChange} sx={{ mb: 3 }}>
                    <TabList>
                        <Tab sx={{ width: '100%' }}>Вход</Tab>
                        <Tab sx={{ width: '100%' }}>Регистрация</Tab>
                    </TabList>
                </Tabs>

                {/* Форма входа */}
                {activeTab === 0 && (
                    <form onSubmit={handleLoginSubmit}>
                        {loginErrors.submit && (
                            <Alert color="danger" sx={{ mb: 2 }}>
                                {loginErrors.submit}
                            </Alert>
                        )}

                        <FormControl sx={{ mb: 2 }} error={!!loginErrors.email}>
                            <FormLabel>Email</FormLabel>
                            <Input
                                type="email"
                                value={loginEmail}
                                onChange={(e) => setLoginEmail(e.target.value)}
                                placeholder="your@email.com"
                                disabled={loading}
                            />
                            {loginErrors.email && (
                                <Typography level="body2" color="danger" sx={{ mt: 0.5 }}>
                                    {loginErrors.email}
                                </Typography>
                            )}
                        </FormControl>

                        <FormControl sx={{ mb: 3 }} error={!!loginErrors.password}>
                            <FormLabel>Пароль</FormLabel>
                            <Input
                                type="password"
                                value={loginPassword}
                                onChange={(e) => setLoginPassword(e.target.value)}
                                placeholder="Введите пароль"
                                disabled={loading}
                            />
                            {loginErrors.password && (
                                <Typography level="body2" color="danger" sx={{ mt: 0.5 }}>
                                    {loginErrors.password}
                                </Typography>
                            )}
                        </FormControl>

                        <Button
                            type="submit"
                            loading={loading}
                            disabled={loading}
                            fullWidth
                            size="lg"
                            sx={{ mb: 2 }}
                        >
                            Войти
                        </Button>
                    </form>
                )}

                {/* Форма регистрации */}
                {activeTab === 1 && (
                    <form onSubmit={handleRegisterSubmit}>
                        {registerErrors.submit && (
                            <Alert color="danger" sx={{ mb: 2 }}>
                                {registerErrors.submit}
                            </Alert>
                        )}

                        <FormControl sx={{ mb: 2 }} error={!!registerErrors.email}>
                            <FormLabel>Email *</FormLabel>
                            <Input
                                type="email"
                                value={registerEmail}
                                onChange={(e) => setRegisterEmail(e.target.value)}
                                placeholder="your@email.com"
                            />
                            {registerErrors.email && (
                                <Typography level="body2" color="danger" sx={{ mt: 0.5 }}>
                                    {registerErrors.email}
                                </Typography>
                            )}
                        </FormControl>

                        <FormControl sx={{ mb: 2 }} error={!!registerErrors.telegram}>
                            <FormLabel>Telegram *</FormLabel>
                            <Input
                                type="text"
                                value={registerTelegram}
                                onChange={(e) => setRegisterTelegram(e.target.value)}
                                placeholder="@username"
                            />
                            {registerErrors.telegram && (
                                <Typography level="body2" color="danger" sx={{ mt: 0.5 }}>
                                    {registerErrors.telegram}
                                </Typography>
                            )}
                        </FormControl>

                        <FormControl sx={{ mb: 2 }} error={!!registerErrors.password}>
                            <FormLabel>Пароль *</FormLabel>
                            <Input
                                type="password"
                                value={registerPassword}
                                onChange={(e) => setRegisterPassword(e.target.value)}
                                placeholder="Минимум 8 символов"
                            />
                            {registerErrors.password && (
                                <Typography level="body2" color="danger" sx={{ mt: 0.5 }}>
                                    {registerErrors.password}
                                </Typography>
                            )}
                        </FormControl>

                        <FormControl sx={{ mb: 3 }} error={!!registerErrors.confirmPassword}>
                            <FormLabel>Подтверждение пароля *</FormLabel>
                            <Input
                                type="password"
                                value={registerConfirmPassword}
                                onChange={(e) => setRegisterConfirmPassword(e.target.value)}
                                placeholder="Повторите пароль"
                            />
                            {registerErrors.confirmPassword && (
                                <Typography level="body2" color="danger" sx={{ mt: 0.5 }}>
                                    {registerErrors.confirmPassword}
                                </Typography>
                            )}
                        </FormControl>

                        <Button
                            type="submit"
                            loading={loading}
                            disabled={loading}
                            fullWidth
                            size="lg"
                            sx={{ mb: 2 }}
                        >
                            Зарегистрироваться
                        </Button>

                        <Typography level="body2" sx={{ textAlign: 'center', color: 'text.secondary' }}>
                            Регистрируясь, вы соглашаетесь с условиями использования
                        </Typography>
                    </form>
                )}

                {/* <Divider sx={{ my: 2 }}>или</Divider>

                <Box sx={{ textAlign: 'center' }}>
                    <Typography level="body2" sx={{ mb: 1 }}>
                        Нет аккаунта?
                    </Typography>
                    <Button
                        component={Link}
                        to="/register"
                        variant="outlined"
                        fullWidth
                    >
                        Зарегистрироваться
                    </Button>
                </Box> */}
            </Card>
        </Box>
    );
}